import { CaretDown } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { ICliJob } from 'cli/types';
import { ActionButton } from 'core/components';
import { AccessPermissions, ActionButtonType, ApiStatus, NotificationPosition } from 'core/enums';
import { ICliJobLostPoll, useCliJobHandler } from 'core/hooks';
import { useNotificationContext } from 'core/states';
import { getLocalDateForServer } from 'core/utils';
import { ExportSkuPartSteps, SendAsSpaConfirmation } from 'quotes/components/ExportSkuPart';
import {
  ISendAsSpaConfirmationState,
  ISendAsSpaResult,
} from 'quotes/components/ExportSkuPart/types';
import {
  getSendAsSpaConcurrentSendNotice,
  getSendAsSpaStatusWriteFailedMessage,
} from 'quotes/components/ExportSkuPart/utils';
import { SpaStatus } from 'quotes/enums';
import { VendorQuoteHelper } from 'quotes/helpers';
import { useQuoteActions } from 'quotes/hooks';
import { IVendorQuote } from 'quotes/types';
import { Color, Size, TooltipPosition, Variant } from 'theme/enums';

interface IProps {
  quote: IVendorQuote;
}

// Split button on the VQ detail: the main part is the one-click direct Send as SPA (same send path
// as the bulk button); the caret menu's Preview opens the same drawer as Download > Export SKU
// Parts. Visibility + send gating mirror the export drawer's canSendAsSpa.
export const SendAsSpaButton = ({ quote }: IProps) => {
  const [isSending, setIsSending] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState<ISendAsSpaConfirmationState | null>(
    null
  );

  const { t } = useTranslation();

  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { showNotification, clearNotification } = useNotificationContext();
  const { handleCliJob } = useCliJobHandler();
  const { sendVendorQuoteAsSpa } = useQuoteActions();
  const queryClient = useQueryClient();

  const canSendAsSpa =
    VendorQuoteHelper.isSkuExportAvailable({ quote, user }) &&
    hasPermission(AccessPermissions.SEND_AS_SPA);

  const { isResend, isDisabled, tooltip } = VendorQuoteHelper.getSpaSendButtonState({
    quote,
    spaAudienceRights: VendorQuoteHelper.getSpaAudienceRights(user),
    isSending,
  });

  const onSendFinished = (cliJob: ICliJob) => {
    setIsSending(false);

    // The BE writes the terminal trade_agreement_status/error (Sent on success, Failed + error on a
    // rejected upload) before the job reaches "finished", so refetch the quote on BOTH outcomes —
    // otherwise a failed send leaves the detail (Attributes tab + this button's gate) stale.
    queryClient.invalidateQueries(['quote']);
    queryClient.invalidateQueries(['quoteFiles']);

    const result = cliJob.result as ISendAsSpaResult | null;

    const statusWriteFailedMessage = getSendAsSpaStatusWriteFailedMessage({
      result,
      isBulk: false,
      t,
    });

    if (statusWriteFailedMessage) {
      showNotification({
        type: ApiStatus.ERROR,
        title: t('titleError'),
        message: statusWriteFailedMessage,
        position: NotificationPosition.TOP,
      });

      return;
    }

    // The job reports "finished" even on a rejected upload — `result.sent` is the real signal.
    if (result?.sent !== true) {
      const concurrentSendNotice = getSendAsSpaConcurrentSendNotice(result);

      // Prefer the BE error; else point at the step that failed (mfr_part | trade_agreement).
      let message = t('messageSendAsSpaError');
      if (concurrentSendNotice) {
        message = t(concurrentSendNotice.key);
      } else if (result?.error) {
        message = result.error;
      } else if (result?.failedStep) {
        message = t('messageSendAsSpaErrorAtStep', {
          step: result.failedStep.replaceAll('_', ' '),
        });
      }

      showNotification({
        type: concurrentSendNotice?.isWarning ? ApiStatus.WARNING : ApiStatus.ERROR,
        title: t(concurrentSendNotice?.isWarning ? 'titleWarning' : 'titleError'),
        message,
        position: NotificationPosition.TOP,
      });

      return;
    }

    clearNotification();
    showNotification({
      type: ApiStatus.SUCCESS,
      title: t('titleSuccess'),
      message: t(
        (result.resendQuoteIds?.length ?? 0) > 0
          ? 'messageResendAsSpaSuccess'
          : 'messageSendAsSpaSuccess'
      ),
      position: NotificationPosition.TOP,
    });
  };

  const onSendStopped = (cliJob: ICliJob) => {
    setIsSending(false);

    // A stopped/crashed job may still have written a Failed status before dying; refetch so the
    // detail doesn't keep showing the pre-send value.
    queryClient.invalidateQueries(['quote']);

    showNotification({
      type: ApiStatus.ERROR,
      title: t('titleError'),
      message: cliJob.statusDescription || t('messageSendAsSpaError'),
      position: NotificationPosition.TOP,
    });
  };

  // The refetched quote shows what the send wrote so far; a second send while this one still runs
  // is skipped by the BE per-quote send lock.
  const onSendPollFailed = async ({ showLostTrackNotice }: ICliJobLostPoll) => {
    await Promise.all([
      queryClient.invalidateQueries(['quote']),
      queryClient.invalidateQueries(['quoteFiles']),
    ]);

    setIsSending(false);
    showLostTrackNotice();
  };

  const send = (localDate: string) => {
    setIsSending(true);

    // Each send pushes to Flintfox, so block re-click while in flight. handleCliJob swallows an
    // enqueue-time throw without hitting the terminal callbacks, so reset the guard on that path
    // too (the callbacks reset it once the job reaches a terminal status).
    const enqueue = async (payload: { quoteId: number; localDate: string }) => {
      try {
        return await sendVendorQuoteAsSpa(payload);
      } catch (error) {
        setIsSending(false);
        throw error;
      }
    };

    handleCliJob(
      enqueue,
      { quoteId: quote.id, localDate },
      {
        cliJobInProgressMessage: t(
          isResend ? 'messageResendAsSpaInProgress' : 'messageSendAsSpaInProgress'
        ),
        cliJobErrorMessage: t('messageSendAsSpaError'),
        notificationPosition: NotificationPosition.TOP,
        onCliJobFinished: onSendFinished,
        onCliJobStopped: onSendStopped,
        onCliJobPollFailed: onSendPollFailed,
        preferBackendError: true,
      }
    );
  };

  // A resend ends the live agreement in Flintfox and a past Valid Until needs the user's word, so
  // either is confirmed first.
  const onClick = () => {
    const localDate = getLocalDateForServer();
    const pastValidUntil = VendorQuoteHelper.isValidUntilInPast(quote, localDate)
      ? VendorQuoteHelper.getValidUntilDay(quote)
      : null;

    if (isResend || pastValidUntil) {
      setSendConfirmation({ localDate, pastValidUntil });

      return;
    }

    send(localDate);
  };

  const onSendConfirm = () => {
    if (!sendConfirmation) {
      return;
    }

    setSendConfirmation(null);
    send(sendConfirmation.localDate);
  };

  if (!canSendAsSpa) {
    return null;
  }

  return (
    <>
      <ActionButton
        action={{
          type: ActionButtonType.GROUP,
          buttons: [
            {
              type: ActionButtonType.BUTTON,
              label: t(isResend ? 'buttonLabelResendAsSpa' : 'buttonLabelSendAsSpa'),
              onClick,
              disabled: isDisabled,
              // Blocked = muted/outlined (greyed next to Order All), enabled = solid.
              variant: isDisabled ? Variant.OUTLINED : Variant.SOLID,
              tooltipProps: tooltip
                ? {
                    placement: TooltipPosition.TOP,
                    color: Color.NEUTRAL,
                    size: Size.SM,
                    title: t(tooltip.key, tooltip.options),
                    children: null,
                  }
                : undefined,
              // MUI swallows hover on disabled buttons; re-enable pointer events so the tooltip
              // shows.
              sx: tooltip
                ? {
                    pointerEvents: 'auto !important',
                    cursor: 'not-allowed !important',
                    '&:hover': { backgroundColor: 'transparent !important' },
                  }
                : undefined,
            },
            {
              type: ActionButtonType.MENU,
              icon: <CaretDown weight="fill" />,
              endDecorator: null,
              // Solid while Preview is available, even when Send is blocked; only during a send,
              // when both halves are disabled, it goes outlined with the main button.
              variant: isSending ? Variant.OUTLINED : Variant.SOLID,
              // Preview stays available while Send is blocked (already sent / missing attributes)
              // — the drawer gates its own send. Only an in-flight direct send locks it, so the
              // drawer cannot start a second send of the same quote.
              disabled: isSending,
              tooltipProps: isSending
                ? {
                    placement: TooltipPosition.TOP,
                    color: Color.NEUTRAL,
                    size: Size.SM,
                    title: t('tooltipSendAsSpaPreviewSendInProgress'),
                    children: null,
                  }
                : undefined,
              menuItems: [
                {
                  label: t('buttonLabelPreview'),
                  onClick: () => setIsPreviewOpen(true),
                },
              ],
            },
          ],
        }}
      />

      <ExportSkuPartSteps
        quote={quote}
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />

      <SendAsSpaConfirmation
        isOpen={sendConfirmation !== null}
        isResend={isResend}
        sentVersion={quote.tradeAgreementSentVersion}
        pastValidUntil={sendConfirmation?.pastValidUntil}
        spaStatus={quote.attributeSpaStatus ?? quote.attributes?.spaStatus ?? SpaStatus.PLANNING}
        onConfirm={onSendConfirm}
        onCancel={() => setSendConfirmation(null)}
      />
    </>
  );
};
